import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { categories, type CategoryType } from '@/mocks/movies';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/category/movie?search=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
    }
  }, [searchValue, navigate]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-film-line text-lg text-white"></i>
            </div>
            <span className={`font-heading font-bold text-xl whitespace-nowrap ${
              isScrolled || !isHome ? 'text-foreground-950' : 'text-white'
            }`}>
              777影院
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={`/category/${cat.key}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isScrolled || !isHome
                    ? 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {cat.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearch} className={`relative transition-all duration-300 ${
              searchFocused ? 'w-64' : 'w-48'
            }`}>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="搜索影视..."
                className={`w-full pl-10 pr-4 py-2 rounded-full text-sm border outline-none transition-all duration-200 ${
                  isScrolled || !isHome
                    ? 'bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400'
                    : 'bg-white/15 border-white/20 text-white placeholder:text-white/50 focus:bg-white/25 focus:border-white/40'
                }`}
              />
              <button type="submit" className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                isScrolled || !isHome ? 'text-foreground-400' : 'text-white/60'
              }`}>
                <i className="ri-search-line text-sm"></i>
              </button>
            </form>
            <Link
              to="/about"
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                isScrolled || !isHome
                  ? 'text-foreground-600 hover:text-primary-500'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
              isScrolled || !isHome ? 'text-foreground-700' : 'text-white'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 bg-background-50/95 backdrop-blur-md pb-4 pt-2">
            <nav className="flex flex-col gap-1 mb-3">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50 transition-colors duration-200"
                >
                  <i className={`${cat.icon} mr-2`}></i>
                  {cat.label}
                </Link>
              ))}
            </nav>
            <form onSubmit={(e) => { handleSearch(e); setMobileMenuOpen(false); }} className="px-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="搜索影视..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-full text-sm bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 outline-none transition-colors"
                />
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              </div>
            </form>
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="block mt-2 px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors duration-200"
            >
              关于我们
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}